import { useCallback, type Dispatch, type SetStateAction } from "react";
import type {
  CmmdCampaign,
  CmmdProject,
  CmmdTask,
  CmmdTaskStatus,
  EnvironmentApi,
  EnvironmentId,
  ProjectId,
  ServerProvider,
  ThreadId,
  ThreadRuntimeOption,
  ThreadEnvMode,
} from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "~/environmentApi";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary";
import { resolveProjectImportRuntimeTarget } from "~/lib/threadRuntimeTarget";
import { createCmmdProjectProject } from "../lib/addProjectSources";
import {
  isForgeAuthExpiredError,
  redirectToForgeLoginAfterAuthExpired,
} from "../lib/authExpiredRedirect";
import { runCmmdSync } from "../hooks/useCmmdSync";
import type { Project } from "../types";
import { toastManager } from "./ui/toast";
import type { ManualCommandTask, NoActiveThreadCommandTaskRow } from "./NoActiveThreadState.logic";
import {
  buildMissingForgeProjectPrompt as buildMissingForgeProjectPromptState,
  resolveImportedCmmdProjectRef as resolveImportedCmmdProjectRefState,
  resolveProjectRefFromRepoState,
  sanitizeCmmdTaskSyncError,
} from "./NoActiveThreadState.logic";
import {
  buildTaskThreadStartPayload,
  type TaskThreadStartPayload,
} from "./NoActiveThreadState.threadLaunch";
import { startThreadForItem } from "./NoActiveThreadState.threadRunner";
import { runTransactionalStartThread } from "./command-center/startThreadFlow";
import type {
  MissingForgeProjectPromptState,
  StartThreadPromptState,
} from "./NoActiveThreadStateDialogs";
import { sanitizePublicUiError } from "~/productSafeLabels";

interface ProjectRef {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
}

interface WorkspaceProjectRef extends Pick<
  Project,
  "environmentId" | "id" | "cwd" | "repositoryIdentity"
> {
  readonly id: ProjectId;
  readonly environmentId: EnvironmentId;
}

export interface UseNoActiveThreadThreadActionsInput {
  readonly activeEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly defaultProjectRef: ProjectRef | null;
  readonly projects: readonly Project[];
  readonly workspaceProject: WorkspaceProjectRef | null;
  readonly defaultThreadRuntime: ThreadRuntimeOption;
  readonly defaultThreadEnvMode: ThreadEnvMode;
  readonly serverProviders: readonly ServerProvider[];
  readonly manualTasks: readonly ManualCommandTask[];
  readonly commandTaskRows: readonly NoActiveThreadCommandTaskRow[];
  readonly cmmdTasks: readonly CmmdTask[];
  readonly cmmdProjects: readonly CmmdProject[];
  readonly cmmdCampaigns: readonly CmmdCampaign[];
  readonly cmmdTaskById: ReadonlyMap<string, CmmdTask>;
  readonly cmmdProjectById: ReadonlyMap<string, CmmdProject>;
  readonly cmmdCampaignById: ReadonlyMap<string, CmmdCampaign>;
  readonly cmmdTaskTitleById: ReadonlyMap<string, string>;
  readonly workspaceName: string | null;
  readonly identityDisplayName: string | null;
  readonly missingForgeProjectPrompt: MissingForgeProjectPromptState | null;
  readonly missingForgeProjectImporting: boolean;
  readonly startThreadPrompt: StartThreadPromptState | null;
  readonly setMissingForgeProjectPrompt: Dispatch<
    SetStateAction<MissingForgeProjectPromptState | null>
  >;
  readonly setMissingForgeProjectImporting: Dispatch<SetStateAction<boolean>>;
  readonly setStartThreadPrompt: Dispatch<SetStateAction<StartThreadPromptState | null>>;
  readonly setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  readonly handleUpdateTaskStatus: (
    itemId: string,
    status: CmmdTaskStatus,
    options?: { readonly promptStartThread?: boolean },
  ) => Promise<void>;
  readonly navigateToThread: (input: {
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
  }) => void;
}

export function useNoActiveThreadThreadActions(input: UseNoActiveThreadThreadActionsInput) {
  const {
    activeEnvironmentId,
    primaryEnvironmentId,
    defaultProjectRef,
    projects,
    workspaceProject,
    defaultThreadRuntime,
    defaultThreadEnvMode,
    serverProviders,
    manualTasks,
    commandTaskRows,
    cmmdTasks,
    cmmdProjects,
    cmmdCampaigns,
    cmmdTaskById,
    cmmdProjectById,
    cmmdCampaignById,
    cmmdTaskTitleById,
    workspaceName,
    identityDisplayName,
    missingForgeProjectPrompt,
    missingForgeProjectImporting,
    startThreadPrompt,
    setMissingForgeProjectPrompt,
    setMissingForgeProjectImporting,
    setStartThreadPrompt,
    setSelectedItemId,
    handleUpdateTaskStatus,
    navigateToThread,
  } = input;

  const buildMissingForgeProjectPrompt = useCallback(
    (itemId: string, payloadTitle: string): MissingForgeProjectPromptState | null => {
      return buildMissingForgeProjectPromptState({
        itemId,
        payloadTitle,
        taskById: cmmdTaskById,
        projectById: cmmdProjectById,
      });
    },
    [cmmdProjectById, cmmdTaskById],
  );

  const resolveImportedCmmdProjectRef = useCallback(
    (itemId: string): ProjectRef | null => {
      return resolveImportedCmmdProjectRefState({
        itemId,
        taskById: cmmdTaskById,
        projectById: cmmdProjectById,
        forgeProjects: projects,
      });
    },
    [cmmdProjectById, cmmdTaskById, projects],
  );

  const buildTaskThreadStartPayloadForItem = useCallback(
    async (itemId: string): Promise<TaskThreadStartPayload | null> => {
      return buildTaskThreadStartPayload({
        itemId,
        manualTasks,
        commandTaskRows,
        cmmdTasks,
        cmmdProjects,
        cmmdCampaigns,
        workspaceName,
        identityDisplayName,
        loadTaskComments: (taskId) => cmmdApiClient.getTaskComments(taskId),
        loadTaskAttachments: (taskId) => cmmdApiClient.getTaskAttachments(taskId),
      });
    },
    [
      cmmdCampaigns,
      cmmdProjects,
      cmmdTasks,
      commandTaskRows,
      identityDisplayName,
      manualTasks,
      workspaceName,
    ],
  );

  const startThreadForItemInSidebar = useCallback(
    async (
      itemId: string,
      importedProjectRef?: ProjectRef,
      options?: { readonly importedFromCmmdProject?: boolean },
    ) => {
      const payload = await buildTaskThreadStartPayloadForItem(itemId);
      if (!payload) {
        throw new Error("Task context was unavailable.");
      }

      return startThreadForItem({
        itemId,
        importedProjectRef,
        importedFromCmmdProject: options?.importedFromCmmdProject === true,
        payload,
        workspaceProject,
        defaultProjectRef,
        projects,
        primaryEnvironmentId,
        defaultThreadRuntime,
        serverProviders,
        cmmdTaskTitleById,
        resolveImportedCmmdProjectRef,
        readEnvironmentApi: readEnvironmentApi as (
          environmentId: EnvironmentId,
        ) => EnvironmentApi | undefined,
      });
    },
    [
      buildTaskThreadStartPayloadForItem,
      cmmdTaskTitleById,
      defaultProjectRef,
      defaultThreadRuntime,
      primaryEnvironmentId,
      projects,
      resolveImportedCmmdProjectRef,
      serverProviders,
      workspaceProject,
    ],
  );

  const handleStartThread = useCallback(
    async (itemId: string) => {
      const payload = await buildTaskThreadStartPayloadForItem(itemId);
      if (!payload) return;

      const rawId = itemId.startsWith("cmmd:") ? itemId.slice(5) : itemId;
      const cmmdTask = cmmdTaskById.get(rawId);
      if (cmmdTask?.projectId && !workspaceProject) {
        const resolvedRef = await resolveLinkedProjectRef(cmmdTask.projectId, projects);
        if (!resolvedRef) {
          const prompt = buildMissingForgeProjectPrompt(itemId, payload.title);
          if (prompt) {
            setMissingForgeProjectPrompt(prompt);
            return;
          }
        } else {
          return runStartThreadTransaction({
            itemId,
            startThreadForItemInSidebar: (id) => startThreadForItemInSidebar(id, resolvedRef),
            handleUpdateTaskStatus,
            setSelectedItemId,
            setMissingForgeProjectPrompt,
            navigateToThread,
          });
        }
      }

      const prompt = buildMissingForgeProjectPrompt(itemId, payload.title);
      const hasLaunchProject =
        workspaceProject !== null ||
        (prompt ? resolveImportedCmmdProjectRef(itemId) !== null : defaultProjectRef !== null) ||
        (!prompt && projects.length > 0);
      if (!hasLaunchProject && prompt) {
        setMissingForgeProjectPrompt(prompt);
        return;
      }
      return runStartThreadTransaction({
        itemId,
        startThreadForItemInSidebar,
        handleUpdateTaskStatus,
        setSelectedItemId,
        setMissingForgeProjectPrompt,
        navigateToThread,
      });
    },
    [
      buildMissingForgeProjectPrompt,
      buildTaskThreadStartPayloadForItem,
      cmmdTaskById,
      defaultProjectRef,
      handleUpdateTaskStatus,
      navigateToThread,
      projects,
      resolveImportedCmmdProjectRef,
      setMissingForgeProjectPrompt,
      setSelectedItemId,
      startThreadForItemInSidebar,
      workspaceProject,
    ],
  );

  const handleImportMissingForgeProject = useCallback(async () => {
    const prompt = missingForgeProjectPrompt;
    if (!prompt || missingForgeProjectImporting) return;

    const cmmdProject = cmmdProjectById.get(prompt.cmmdProjectId);
    if (!cmmdProject) {
      toastManager.add({
        type: "error",
        title: "CMMD project unavailable",
        description: "Refresh CMMD sync and try importing this project again.",
      });
      return;
    }

    const target = resolveProjectImportRuntimeTarget({
      requestedRuntime: defaultThreadRuntime,
      activeEnvironmentId,
      primaryEnvironmentId,
      readEnvironmentApi,
    });
    if (!target) {
      toastManager.add({
        type: "error",
        title: "Command environment unavailable",
        description: "Forge needs an active Command environment before it can import projects.",
      });
      return;
    }

    setMissingForgeProjectImporting(true);
    try {
      const result = await createCmmdProjectProject({
        api: target.api,
        environmentId: target.environmentId,
        projects,
        defaultThreadEnvMode,
        handleNewThread: async () => undefined,
        resolveHttpUrl: resolvePrimaryEnvironmentHttpUrl,
        project: cmmdProject,
        campaignName: cmmdCampaignById.get(cmmdProject.campaignId ?? "")?.title ?? null,
      });

      await runCmmdSync({ forceFetch: true });
      setMissingForgeProjectPrompt(null);
      await runStartThreadTransaction({
        itemId: prompt.itemId,
        startThreadForItemInSidebar: (itemId) =>
          startThreadForItemInSidebar(
            itemId,
            {
              environmentId: target.environmentId,
              projectId: result.projectId,
            },
            { importedFromCmmdProject: true },
          ),
        handleUpdateTaskStatus,
        setSelectedItemId,
        setMissingForgeProjectPrompt,
        fallbackEnvironmentId: target.environmentId,
        navigateToThread: (thread) =>
          navigateToThread({
            environmentId: thread.environmentId,
            threadId: thread.threadId,
          }),
      });
    } catch (error) {
      if (isForgeAuthExpiredError(error)) {
        void redirectToForgeLoginAfterAuthExpired();
        return;
      }
      toastManager.add({
        type: "error",
        title: "Could not import project",
        description:
          error instanceof Error
            ? sanitizeCmmdTaskSyncError(error)
            : "Forge could not import this CMMD project.",
      });
    } finally {
      setMissingForgeProjectImporting(false);
    }
  }, [
    activeEnvironmentId,
    cmmdCampaignById,
    cmmdProjectById,
    defaultThreadEnvMode,
    defaultThreadRuntime,
    handleUpdateTaskStatus,
    missingForgeProjectImporting,
    missingForgeProjectPrompt,
    navigateToThread,
    primaryEnvironmentId,
    projects,
    setMissingForgeProjectImporting,
    setMissingForgeProjectPrompt,
    setSelectedItemId,
    startThreadForItemInSidebar,
  ]);

  const confirmStartThreadPrompt = useCallback(() => {
    if (!startThreadPrompt) return;
    const pendingPrompt = startThreadPrompt;
    setStartThreadPrompt(null);
    void startThreadForItemInSidebar(pendingPrompt.itemId)
      .then((result) => {
        if (result.autoStarted) {
          toastManager.add({
            type: "success",
            title: "Thread started",
            description: "A new thread has been created in the sidebar for this task.",
          });
          return;
        }
        toastManager.add({
          type: "warning",
          title: "Thread created, auto-start skipped",
          description:
            result.providerHint ??
            "The selected provider is currently unavailable. Open the thread and choose an available provider to continue.",
        });
      })
      .catch((error) => {
        toastManager.add({
          type: "error",
          title: "Could not start thread",
          description: sanitizePublicUiError(error, "An unexpected error occurred."),
        });
      });
  }, [setStartThreadPrompt, startThreadForItemInSidebar, startThreadPrompt]);

  return {
    handleStartThread,
    handleImportMissingForgeProject,
    confirmStartThreadPrompt,
  };
}

async function resolveLinkedProjectRef(
  cmmdProjectId: string,
  projects: readonly Project[],
): Promise<ProjectRef | null> {
  try {
    const repoResult = await cmmdApiClient.getProjectRepository(cmmdProjectId);
    return resolveProjectRefFromRepoState({ repoResult, projects });
  } catch {
    return null;
  }
}

function runStartThreadTransaction(input: {
  readonly itemId: string;
  readonly startThreadForItemInSidebar: (itemId: string) => ReturnType<typeof startThreadForItem>;
  readonly handleUpdateTaskStatus: UseNoActiveThreadThreadActionsInput["handleUpdateTaskStatus"];
  readonly setSelectedItemId: Dispatch<SetStateAction<string | null>>;
  readonly setMissingForgeProjectPrompt: Dispatch<
    SetStateAction<MissingForgeProjectPromptState | null>
  >;
  readonly fallbackEnvironmentId?: EnvironmentId;
  readonly navigateToThread: (input: {
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
  }) => void;
}) {
  return runTransactionalStartThread(input.itemId, {
    startThreadForItemInSidebar: input.startThreadForItemInSidebar,
    updateTaskStatus: input.handleUpdateTaskStatus,
    onThreadCreated: (result) => {
      if (!result.threadId) return;
      const environmentId = result.environmentId ?? input.fallbackEnvironmentId;
      if (!environmentId) return;
      input.setSelectedItemId(null);
      input.setMissingForgeProjectPrompt(null);
      input.navigateToThread({
        environmentId: environmentId as EnvironmentId,
        threadId: result.threadId as ThreadId,
      });
    },
    toast: (toast) => toastManager.add(toast),
  });
}
